import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { AvailabilityCalendar } from "@/components/availability/AvailabilityCalendar";
import { StayPicker, type StayValue } from "@/components/availability/StayPicker";
import { computeAvailableFrom, fromIso, stayEnd, toIso, validateStay } from "@/lib/availability";
import { absentEssentials, groupedAmenities, presentAmenityLabels } from "@/lib/amenities";
import { useCollections } from "@/hooks/useCollections";
import { useBrowseContext } from "@/hooks/useBrowseContext";
import { collectionForAmenity, collectionPath } from "@/lib/collections";
import AmenityIcon from "@/components/listing/AmenityIcon";
import { lastBrowsePath } from "@/lib/lastBrowseSearch";
import { resolveStorageUrl, transformImage } from "@/lib/imageUrl";
import { hasAnalyticsConsent } from "@/lib/cookieConsent";
import { getTrackingContext, track } from "@/lib/track";
import { ArrowLeft, Bed, Bath, Ruler, MapPin, Check, X as XIcon, Users, Lock, Clock, CalendarDays, Pencil, Footprints, Car, CalendarIcon, Sparkles, Loader2, CigaretteOff } from "lucide-react";
import { PageContainer } from "@/components/ui/page-container";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProperty, type Property } from "@/hooks/useProperties";
import ListingQualityBanner from "@/components/listing/ListingQualityBanner";
import CompleteProfileDialog from "@/components/CompleteProfileDialog";
import { useContactRequirements } from "@/hooks/useQualityConfig";
import { getMissingContactFields, type ContactFieldKey } from "@/lib/profileCompleteness";
import { useTenant } from "@/hooks/useTenant";
import { preferencesForSite } from "@/lib/renterMarkets";
import { useState, useEffect, useMemo } from "react";
import PhotoGallery from "@/components/PhotoGallery";
import PropertyMap from "@/components/PropertyMap";
import LazyOnView from "@/components/LazyOnView";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePromotions } from "@/lib/monetization";
import { toast } from "sonner";
import { format, parseISO, startOfDay, isSameDay, differenceInCalendarDays } from "date-fns";
import { cn } from "@/lib/utils";
import { isPetOption } from "@/lib/listingOptions";
import Seo from "@/components/Seo";
import DelistDialog from "@/components/listing/DelistDialog";
import { isTeamLocked, listingCompletenessIssues, republishListing } from "@/lib/listingLifecycle";
import { reopenContactRequest } from "@/lib/messaging";
import { TENANTS } from "@/lib/tenants";
import { useCopy } from "@/hooks/useSiteCopy";
import { REQUEST_CONTACT_ERROR, requestHasContact, siteHost } from "../../supabase/functions/_shared/requestText";
import { Badge } from "@/components/ui/badge";
import {
  accommodationSchema,
  breadcrumbList,
} from "@/lib/seo";
import {
  scoreProperty,
  getMatchBreakdown,
  hasAnyPreference,
  matchScoreClasses,
  type RenterPreferences,
  type MatchCriterion,
} from "@/lib/matchScore";
import { useMatchConfig } from "@/hooks/useMatchConfig";
import { useIsMobile } from "@/hooks/use-mobile";
import { profileRowToPreferences } from "@/lib/renterPreferences";
import { PRICE_ON_REQUEST, formatRent, hasPrice, tenantCurrency } from "@/lib/money";
import IdentityOffer from "@/components/monetization/IdentityOffer";
import { bedsIn, bedsLabel } from "@/lib/bedrooms";


// Convert a property's location string ("Playa Hermosa", "Mal País") into the
// slug used by /areas/:area routes ("playa-hermosa", "mal-pais").
const locationToSlug = (loc: string) =>
  loc
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const NO_PROPERTIES: Property[] = [];


// The stay as it is sent: months clamped the way the picker shows them. A
// stay fits when no dates were picked or the listing can take it; the
// database refuses the rest (contact_requests_check_dates).
const stayMonths = (p: Property, s: StayValue) => Math.min(12, Math.max(Math.max(1, p.minRentalMonths || 1), s.months || 1));
const stayFits = (p: Property, s: StayValue) => !s.moveIn || validateStay(p, s.moveIn, stayMonths(p, s)).ok;

// Our own sites: a link to one of them is always fine in a request.
const OWN_SITE_HOSTS = TENANTS.map((t) => siteHost(t.siteUrl));

// Homes already counted this page load, for visitors without analytics consent
// (no sessionStorage for them).
const viewedWithoutConsent = new Set<string>();

const PropertyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftToken = searchParams.get("draft");
  const previewParam = searchParams.get("preview") === "1";
  const tenant = useTenant();
  const { collections } = useCollections();
  const browseCtx = useBrowseContext(NO_PROPERTIES);
  const { data: property, isLoading, error } = useProperty(slug);
  const { user, loading: authLoading } = useAuth();
  const promotions = usePromotions();
  const isFeatured = !!property && promotions.featured.has(property.id);
  const { weights } = useMatchConfig();
  const [showContactRequest, setShowContactRequest] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  // Links, handles, emails and payment details can't go in a request (Oct 18).
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestStatus, setRequestStatus] = useState<"none" | "pending" | "accepted" | "declined" | "closed">("none");
  // Declined by the team for the rules (a held pitch), not by the owner (Oct 19).
  const [declinedForRules, setDeclinedForRules] = useState(false);
  const rulesNote = useCopy("request.rules-note");
  // A request the system closed (listing paused / dates changed) that the
  // renter may send again.
  const [closedRequest, setClosedRequest] = useState<{ id: string; reason: string | null; moveIn: string | null; moveOut: string | null } | null>(null);
  const [delistOpen, setDelistOpen] = useState(false);
  // ?draft=<claim_token>: only a token the DB recognises unlocks the gates.
  const [claimTokenValid, setClaimTokenValid] = useState<boolean | null>(null);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [ownerProfile, setOwnerProfile] = useState<any>(null);
  const [renterProfile, setRenterProfile] = useState<any>(null);
  // Renter's intended stay (Airbnb-monthly style): move-in + months; the
  // request carries the window so the owner can see it at a glance.
  const [stay, setStay] = useState<StayValue>({ moveIn: undefined, months: 1 });
  const [matchScore, setMatchScore] = useState<number | null>(null);
  const [matchBreakdown, setMatchBreakdown] = useState<MatchCriterion[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [localStatus, setLocalStatus] = useState<string | null>(null);
  // Quick-complete gate state: which required fields are missing, and the
  // renter's phone (null = not fetched yet; lives on profile_contacts, so it
  // needs its own RPC read).
  const [completeProfileOpen, setCompleteProfileOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<ContactFieldKey[]>([]);
  const [renterPhone, setRenterPhone] = useState<string | null>(null);
  // Per-tenant "what must a renter fill before contacting" (admin Quality
  // control panel); falls back to name+phone+introduction while loading.
  const { data: contactReqs } = useContactRequirements(tenant.id);

  const isOwner = !!user && !!property?.owner_id && user.id === property.owner_id;
  // A valid claim link (?draft=<token>) or the owner's own "preview as a
  // renter" (?preview=1) renders the public view; admins only get owner-view
  // when they actually own the listing.
  const validDraft = !!draftToken && claimTokenValid === true;
  const previewMode = previewParam && (isOwner || isAdmin);
  const isOwnerView = isOwner && !validDraft && !previewMode;
  const currentStatus = localStatus ?? property?.status ?? "available";
  const isPublished = currentStatus === "available";
  // A request needs free dates (Felipe, Oct 27): when no stay fits in the
  // next 18 months there is nothing to send.
  const hasFreeDates = useMemo(() => !!property && !!computeAvailableFrom(property), [property]);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle().then(({ data }) => setIsAdmin(!!data));
  }, [user]);

  useEffect(() => {
    if (!draftToken || !property?.id) { setClaimTokenValid(null); return; }
    let cancelled = false;
    supabase.rpc("get_property_by_claim_token", { _token: draftToken }).then(({ data }) => {
      if (cancelled) return;
      const rows = (Array.isArray(data) ? data : data ? [data] : []) as { id: string }[];
      setClaimTokenValid(rows.some((r) => r.id === property.id));
    });
    return () => { cancelled = true; };
  }, [draftToken, property?.id]);

  // One view per session per listing, never the owner's own visits, and only
  // once auth has settled so a signed-in visitor isn't counted twice
  // (anonymous, then signed in). Session context makes views joinable to
  // sessions, devices and campaigns in the admin metrics.
  useEffect(() => {
    if (!property?.id || authLoading || isOwnerView || isOwner) return;
    // Without analytics consent the view still counts for the owner, but
    // nothing is stored on the device and nothing ties it to a person.
    if (!hasAnalyticsConsent()) {
      if (viewedWithoutConsent.has(property.id)) return;
      viewedWithoutConsent.add(property.id);
      void supabase.from("property_views").insert({
        property_id: property.id,
        tenant_id: tenant.id,
        device: getTrackingContext().device,
      }).then(({ error }) => { if (error) console.warn("property_views insert failed", error); });
      return;
    }
    const key = `msth_viewed_${property.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      /* no storage, count the view anyway */
    }
    const ctx = getTrackingContext();
    void supabase.from("property_views").insert({
      property_id: property.id,
      user_id: user?.id || null,
      tenant_id: tenant.id,
      session_id: ctx.sessionId,
      visitor_id: ctx.visitorId,
      device: ctx.device,
      referrer_host: ctx.referrerHost,
      utm: ctx.utm,
    }).then(({ error }) => { if (error) console.warn("property_views insert failed", error); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id, authLoading, isOwnerView]);
  // maybeSingle so an RLS-filtered zero-row response (guests don't have
  // anon SELECT on profiles) returns null cleanly instead of a 406.
  // Prefill the stay from the renter's profile once; months from the listing's
  // minimum when the profile has no move-out yet.
  useEffect(() => {
    if (!user || !property?.id || isOwnerView) return;
    let cancelled = false;
    supabase
      .from("profiles")
      .select("move_in_date, move_out_date")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data) return;
        const moveIn = data.move_in_date ? fromIso(data.move_in_date) : undefined;
        const minMonths = Math.max(1, property.minRentalMonths || 1);
        const months =
          data.move_in_date && data.move_out_date
            ? Math.max(minMonths, Math.min(12, Math.round(differenceInCalendarDays(fromIso(data.move_out_date), fromIso(data.move_in_date)) / 30.4) || minMonths))
            : minMonths;
        // A saved move-in that this home can't take (passed, blocked) is not
        // carried over; the renter picks one the calendar allows.
        setStay({ moveIn: moveIn && validateStay(property, moveIn, months).ok ? moveIn : undefined, months });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, property?.id, isOwnerView]);

  useEffect(() => { if (property?.owner_id) supabase.from("profiles").select("display_name, bio, avatar_url").eq("user_id", property.owner_id).maybeSingle().then(({ data }) => setOwnerProfile(data)); }, [property?.owner_id]);

  useEffect(() => {
    if (!user || !property?.id || isOwnerView) return;
    supabase
      .from("profiles")
      .select(
        "display_name, introduction, profile_completed, preferred_budget_min, preferred_budget_max, preferred_areas, preferred_types, preferred_bedrooms, preferred_bathrooms, preferred_amenities, preferred_services, move_in_date, move_out_date, num_people, has_pets, preferred_dealbreakers"
      )
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        setRenterProfile(data);
        if (!data || !property) {
          setMatchScore(null);
          setMatchBreakdown([]);
          return;
        }
        // Only this market's areas count on this site (Oct 17, renterMarkets.ts).
        const prefs: RenterPreferences = preferencesForSite(profileRowToPreferences(data),
          { id: tenant.id, areas: tenant.areas, areaSlugs: tenant.areaSlugs });
        if (hasAnyPreference(prefs)) {
          setMatchScore(scoreProperty(property, prefs, weights));
          setMatchBreakdown(getMatchBreakdown(property, prefs));
        } else {
          setMatchScore(null);
          setMatchBreakdown([]);
        }
      });
    supabase.from("contact_requests").select("*").eq("property_id", property.id).eq("renter_id", user.id).maybeSingle().then(({ data }) => {
      if (!data) return;
      const row = data as unknown as {
        id: string; status: string; closed_reason?: string | null; auto_closed_at?: string | null;
        move_in_date?: string | null; move_out_date?: string | null;
      };
      if (row.status === "declined" && row.auto_closed_at) {
        setClosedRequest({ id: row.id, reason: row.closed_reason ?? null, moveIn: row.move_in_date ?? null, moveOut: row.move_out_date ?? null });
        // Start the re-request from the dates they asked for, so "Request
        // again" never silently drops them.
        if (row.move_in_date && row.move_out_date) {
          const start = fromIso(row.move_in_date);
          const months = Math.max(1, Math.round(differenceInCalendarDays(fromIso(row.move_out_date), start) / 30.4));
          setStay({ moveIn: start, months });
        }
        setRequestStatus("closed");
      } else {
        // A request held for the team reads as pending to the renter (Oct 18).
        setRequestStatus((row.status === "held" ? "pending" : row.status) as "pending" | "accepted" | "declined");
        setDeclinedForRules(row.status === "declined" && row.closed_reason === "held:not_allowed");
      }
    });
  }, [user, property, isOwnerView, weights]);

  // The actual send, no gate here. Called directly when the profile passes
  // the tenant's contact requirements, or from the quick-complete dialog's
  // onCompleted right after the missing fields are saved.
  const submitContactRequest = async () => {
    if (!user || !property) return;
    if (!stay.moveIn) {
      toast.error("Pick your move-in date first");
      return;
    }
    if (!stayFits(property, stay)) {
      toast.error("Those dates don't fit this home. Pick other dates.");
      return;
    }
    const months = stayMonths(property, stay);
    if (requestHasContact(requestMessage, OWN_SITE_HOSTS)) {
      setRequestError(REQUEST_CONTACT_ERROR);
      return;
    }
    // Re-sending a request the system closed: same row, new dates, back to
    // pending (the DB checks the dates fit the listing).
    if (closedRequest) {
      setSendingRequest(true);
      try {
        await reopenContactRequest(
          closedRequest.id,
          stay.moveIn ? toIso(stay.moveIn) : null,
          stay.moveIn ? toIso(stayEnd(stay.moveIn, months)) : null,
          requestMessage.trim() || null,
        );
      } catch (e) {
        setSendingRequest(false);
        toast.error(e instanceof Error ? e.message : "Couldn't send the request");
        return;
      }
      setSendingRequest(false);
      setRequestStatus("pending"); setShowContactRequest(false);
      toast.success("Request sent again! Waiting for the owner's response.");
      track("request_reopened", { property_id: property.id, has_dates: !!stay.moveIn, months });
      supabase.functions.invoke("notify-owner-contact-request", { body: { request_id: closedRequest.id, reopened: true } })
        .catch((e) => console.warn("Failed to notify owner of reopened request", e));
      setClosedRequest(null);
      return;
    }
    setSendingRequest(true);
    const fullMessage = requestMessage.trim();
    // One server door (send-contact-request, Oct 18): it refuses links and
    // handles, holds a pitch for the team, and tells the owner.
    const { data: sent, error } = await supabase.functions.invoke("send-contact-request", {
      body: {
        propertyId: property.id,
        message: fullMessage,
        moveIn: stay.moveIn ? toIso(stay.moveIn) : null,
        moveOut: stay.moveIn ? toIso(stayEnd(stay.moveIn, months)) : null,
      },
    });
    setSendingRequest(false);
    if (error || !sent?.id) {
      const body = (await (error as { context?: Response } | null)?.context?.json?.().catch(() => null)) as { error?: string; message?: string } | null;
      if (body?.error === "already_sent") toast.info("You've already sent a request for this property");
      else if (body?.error === "has_contact") setRequestError(body.message ?? REQUEST_CONTACT_ERROR);
      else toast.error(body?.message ?? "Couldn't send the request. Try again in a moment.");
      return;
    }
    setRequestStatus("pending"); setShowContactRequest(false);
    toast.success("Request sent! Waiting for owner response.", { description: "The owner will review your profile and respond." });
    track("contact_request_sent", { property_id: property.id, has_dates: !!stay.moveIn, months });
  };

  const handleContactRequest = async () => {
    if (!user || !property) return;
    // Resolve the gate inputs lazily: the profile effect may not have resolved
    // on a fast click, and the phone lives on the PII-isolated
    // profile_contacts table (own-row RPC), not on profiles.
    let prof = renterProfile;
    if (!prof) {
      const { data } = await supabase
        .from("profiles")
        .select("display_name, introduction, profile_completed")
        .eq("user_id", user.id)
        .maybeSingle();
      prof = data;
      if (data && !renterProfile) setRenterProfile(data);
    }
    let phone = renterPhone;
    if (phone === null) {
      const { data: ph } = await supabase.rpc("get_own_phone");
      phone = (ph as string | null) ?? "";
      setRenterPhone(phone);
    }

    const missing = getMissingContactFields(
      { displayName: prof?.display_name, phone, introduction: prof?.introduction },
      contactReqs,
    );
    if (missing.length > 0) {
      // The typed message stays in requestMessage state under the dialog,
      // after the save it is sent automatically, nothing is lost.
      setMissingFields(missing);
      setCompleteProfileOpen(true);
      return;
    }
    // All required fields present, ratchet the legacy flag if it lags (it's
    // load-bearing for matching/RLS elsewhere), then send.
    if (prof && !prof.profile_completed) {
      void supabase
        .from("profiles")
        .update({ profile_completed: true })
        .eq("user_id", user.id)
        .then(({ error }) => { if (error) console.warn("profile_completed sync failed", error); });
      setRenterProfile((p) => (p ? { ...p, profile_completed: true } : p));
    }
    await submitContactRequest();
  };

  // Pausing asks why (DelistDialog); publishing runs the shared completeness
  // check and goes through the lifecycle helper so renters get alerted and
  // closed-request emails are drained.
  const handleTogglePublish = async () => {
    if (!property) return;
    if (isPublished) { setDelistOpen(true); return; }
    if (isTeamLocked(currentStatus)) { toast.error("This listing was removed by the team"); return; }
    const issues = listingCompletenessIssues({
      title: property.title, location: property.location, type: property.type,
      bedrooms: property.bedrooms, bathrooms: property.bathrooms, price: property.price,
      min_rental_months: property.minRentalMonths, available_date: property.availableDate,
      available_until: property.availableUntil, amenities: property.amenities, description: property.description,
    });
    if (issues.length > 0) {
      toast.error("Finish the listing before publishing", {
        description: issues.join(" · "),
        action: { label: "Finish setup", onClick: () => navigate(`/edit-property/${property.id}`) },
      });
      return;
    }
    setLocalStatus("available");
    const { error, row } = await republishListing(property.id);
    if (error) {
      toast.error("Couldn't publish the listing");
      setLocalStatus(currentStatus);
      return;
    }
    if (row && !row.quality_visible) {
      toast.warning("Published, but hidden by quality checks for now", { description: "Fix the items in the banner and it goes live automatically." });
    } else {
      toast.success("Listing published");
    }
  };

  // Stay in the loading branch until either the query resolves with data or
  // genuinely errors. The previous order (`if (error || !property)`) flashed
  // "Property not found" for ~50-200ms during the React-Query transition on
  // route changes.
  if (isLoading || (!property && !error) || (!!draftToken && !!property && claimTokenValid === null)) {
    return (
      <PageContainer>
        <div className="text-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
        </div>
      </PageContainer>
    );
  }
  if (error || !property) {
    return (
      <PageContainer>
        <div className="text-center py-16">
          <h1 className="font-serif text-2xl mb-3">Property not found</h1>
          <Link to={lastBrowsePath()} className="text-primary underline text-[14px]">Back to listings</Link>
        </div>
      </PageContainer>
    );
  }
  // Multi-tenant guard: if the listing exists in the DB but its tenant_id
  // doesn't match this domain's tenant (e.g. someone shared a Nosara URL
  // on mysantateresahome.com), 404 it. Admins / draft-preview tokens
  // bypass. NULL tenant_id (back-fill miss) is treated as out-of-scope
  //, admin needs to attribute the listing before the public sees it.
  const propTenantId = (property as { tenant_id?: string | null }).tenant_id;
  if (!validDraft && !isAdmin && propTenantId !== tenant.id) {
    return (
      <PageContainer>
        <div className="text-center py-16">
          <h1 className="font-serif text-2xl mb-3">Property not found</h1>
          <Link to={lastBrowsePath()} className="text-primary underline text-[14px]">Back to listings</Link>
        </div>
      </PageContainer>
    );
  }
  // Quality-control gate: a listing hidden by the tenant's QC rules is invisible
  // to renters even via a direct link. Owner, admin, and draft-preview views
  // bypass (the owner sees the "needs improvements" banner instead). No-op
  // until a tenant enables QC, quality_visible defaults to true.
  if (!validDraft && !isAdmin && !isOwner && !property.qualityVisible) {
    return (
      <PageContainer>
        <Seo title={`This listing isn’t currently available | ${tenant.siteName}`} description="This listing is not available right now." path={`/properties/${property.slug}`} noindex />
        <div className="text-center py-16">
          <h1 className="font-serif text-2xl mb-3">This listing isn’t currently available</h1>
          <Link to={lastBrowsePath()} className="text-primary underline text-[14px]">Browse other homes</Link>
        </div>
      </PageContainer>
    );
  }
  // Off-market gate: paused / rented / draft / archived listings are hidden
  // from everyone but the owner, admins and valid claim links, and tell
  // search engines to drop the page.
  if (!validDraft && !isAdmin && !isOwner && currentStatus !== "available") {
    return (
      <PageContainer>
        <Seo title={`This home isn’t available right now | ${tenant.siteName}`} description="This listing is currently off the market." path={`/properties/${property.slug}`} noindex />
        <div className="text-center py-16 max-w-md mx-auto">
          <h1 className="font-serif text-2xl mb-3">This home isn’t available right now</h1>
          <p className="text-[14px] text-muted-foreground mb-6">
            The owner has taken it off the market for now. If it comes back, you’ll be able to send a request again.
          </p>
          <Button asChild className="rounded-full">
            <Link to={lastBrowsePath()}>Browse other homes</Link>
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Declared features grouped the way owners declare them (the legacy
  // booleans count too); pets have their own section below.
  const amenityGroups = groupedAmenities(presentAmenityLabels(property)).filter((g) => g.id !== "pets");
  const notIncluded = absentEssentials(property);
  const activeServices = Object.entries(property.services).filter(([_, v]) => v).map(([k]) => {
    const labels: Record<string, string> = { cleaning: "Cleaning", electricity: "Electricity", gardener: "Gardener", internet: "Internet", poolManager: "Pool Manager", propertyManager: "Property Manager", water: "Water" };
    return labels[k] || k;
  });

  const priced = hasPrice(property.price);
  const priceLabel = priced ? formatRent(property.price, property.priceMax, tenantCurrency(tenant), !!property.priceVariesByStay) : PRICE_ON_REQUEST;

  const minRentalLabel = property.minRentalMonths
    ? `${property.minRentalMonths} ${property.minRentalMonths === 1 ? "month" : "months"}`
    : property.included.minRental;

  const seoTitle = `${property.title} | ${property.bedrooms}BR ${property.type || "rental"} in ${property.location}`;
  const seoDescription = (property.description || `Monthly rental in ${property.location}, ${tenant.regionLabel}. ${property.bedrooms} bedrooms, ${property.bathrooms} bathrooms. From $${property.price.toLocaleString()}/month on ${tenant.siteName}.`)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 300);

  const seoCtx = { siteUrl: tenant.siteUrl, siteName: tenant.siteName };
  // Share the first photo at card size (1200×630, cropped) so scrapers get a
  // small, correctly framed image instead of the full-resolution original.
  const firstPhoto = property.images?.[0];
  const shareImage =
    firstPhoto && !firstPhoto.endsWith("placeholder.svg")
      ? transformImage(firstPhoto, { width: 1200, height: 630, format: "origin", resize: "cover" })
      : undefined;

  return (
    <div className={isOwnerView ? "" : "pb-16 lg:pb-0"}>
      {!isOwnerView && (
        <div className="lg:hidden fixed inset-x-0 bottom-[calc(52px+env(safe-area-inset-bottom))] z-40 border-t bg-card/95 backdrop-blur-sm px-4 py-2 flex items-center justify-between gap-3">
          <p className="text-base font-semibold text-primary truncate">
            {priceLabel}
            <span className="text-[12px] font-normal text-muted-foreground">/month</span>
          </p>
          <Button
            size="sm"
            className="rounded-full shrink-0"
            onClick={() =>
              document.getElementById("contact-owner-card")?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Contact owner
          </Button>
        </div>
      )}
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={`/properties/${property.slug}`}
        image={shareImage}
        imageAlt={property.title}
        imageWidth={shareImage ? 1200 : undefined}
        imageHeight={shareImage ? 630 : undefined}
        ogType="product"
        noindex={currentStatus !== "available" || !property.qualityVisible}
        jsonLd={[
          accommodationSchema(seoCtx, {
            name: property.title,
            description: seoDescription,
            url: `/properties/${property.slug}`,
            images: property.images,
            bedrooms: property.bedrooms,
            bathrooms: property.bathrooms,
            maxGuests: property.maxGuests,
            size: property.size,
            type: property.type,
            petsAllowed: property.petFriendly,
            // The same values the Amenities and Services sections render.
            amenities: presentAmenityLabels(property),
            amenitiesAbsent: notIncluded,
            servicesIncluded: activeServices,
            minRentalMonths: property.minRentalMonths,
            availableFrom: property.availableDate,
            area: property.location,
            region: tenant.addressRegion,
            country: tenant.country,
            latitude: property.coordinates?.[1],
            longitude: property.coordinates?.[0],
            pricePerMonth: property.price,
            currency: tenantCurrency(tenant),
            priceMax: property.priceMax,
            available: currentStatus === "available",
          }),
          breadcrumbList(seoCtx, [
            { name: "Home", path: "/" },
            { name: "Browse rentals", path: "/properties" },
            { name: property.location, path: `/areas/${locationToSlug(property.location)}` },
            { name: property.title, path: `/properties/${property.slug}` },
          ]),
        ]}
      />
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {previewMode && (
          <div className="mb-6 rounded-xl border border-primary/30 bg-ocean-light px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-[13px]">
            <span><span className="font-semibold">Preview mode.</span> This is what renters see. The request form is off for you.</span>
            <Link to={`/properties/${property.slug}`} className="underline underline-offset-2 font-medium">Back to owner view</Link>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <Link to={isOwnerView ? "/dashboard" : lastBrowsePath()} className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> {isOwnerView ? "Back to my listed homes" : "Back to listings"}
          </Link>
          {isOwnerView && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 bg-popover/90 backdrop-blur-sm rounded-full px-3 py-1.5 border shadow-sm">
                <Switch
                  checked={isPublished}
                  onCheckedChange={handleTogglePublish}
                  aria-label={isPublished ? "Unpublish listing" : "Publish listing"}
                />
                <span className="text-[11px] font-semibold tracking-wider uppercase text-foreground">
                  {isPublished ? "Published" : currentStatus === "rented" ? "Rented" : currentStatus === "draft" ? "Draft" : "Paused"}
                </span>
              </div>
              <Button asChild size="sm" variant="outline" className="rounded-full gap-1.5 px-4 py-2 text-[12px] font-semibold tracking-wider uppercase shadow-sm">
                <Link to={`/properties/${property.slug}?preview=1`}><Users className="h-3.5 w-3.5" /> See it as renters see it</Link>
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(`/edit-property/${property.id}`)}
                className="rounded-full gap-1.5 px-4 py-2 text-[12px] font-semibold tracking-wider uppercase shadow-sm"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            </div>
          )}
        </div>

        <PhotoGallery photos={property.photos} title={property.title} />

        {property.videos.length > 0 && (
          <div className="mb-8 -mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {property.videos.map((src, i) => (
                <video
                  key={i}
                  src={src}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full aspect-video rounded-card bg-muted object-cover"
                />
              ))}
            </div>
          </div>
        )}

        {isFeatured && (
          <span data-testid="featured-pill" className="mt-4 inline-flex items-center rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground">Featured</span>
        )}
        {/* SEO H1, public visitors don't see the owner-only title, so we
            render a descriptive heading derived from public fields. */}
        {!isOwnerView && (
          <h1 className="font-serif text-2xl sm:text-3xl font-medium leading-tight mt-4 mb-2">
            {property.bedrooms}-bedroom {property.type?.toLowerCase() || "home"} for rent in {property.location}
          </h1>
        )}

        {/* Key Info Bar */}
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 py-6 mb-6 border-b">
          <p className="text-primary text-2xl font-semibold">
            {priceLabel}{priced && <span className="text-[14px] font-normal text-muted-foreground">/month</span>}
          </p>
          <span className="flex items-center gap-1.5 text-[14px] text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <Link to={`/areas/${locationToSlug(property.location)}`} className="hover:text-primary hover:underline">{property.location}</Link>
          </span>
          <span className="flex items-center gap-1.5 text-[14px] text-muted-foreground"><Bed className="h-4 w-4" /> {property.bedrooms} bd</span>
          <span className="flex items-center gap-1.5 text-[14px] text-muted-foreground"><Bath className="h-4 w-4" /> {property.bathrooms} ba</span>
          <span className="flex items-center gap-1.5 text-[14px] text-muted-foreground"><Users className="h-4 w-4" /> {property.maxGuests} guests</span>
          {property.walkingDistanceToBeachMin != null && (
            <span className="flex items-center gap-1.5 text-[14px] text-muted-foreground"><Footprints className="h-4 w-4" /> {property.walkingDistanceToBeachMin} min walk to beach</span>
          )}
          {property.parkingSpots > 0 && (
            <span className="flex items-center gap-1.5 text-[14px] text-muted-foreground"><Car className="h-4 w-4" /> {property.parkingSpots} parking</span>
          )}
          {property.available.length > 0 && (
            <span className="text-[13px] text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {property.available.length} months available</span>
          )}
        </div>

        <div className={`grid grid-cols-1 ${isOwnerView ? "" : "lg:grid-cols-3"} gap-12`}>
          <div className={`${isOwnerView ? "" : "lg:col-span-2"} space-y-12`}>
            {/* Title is owner-only, only show in owner/admin view */}
            {isOwnerView && (
              <div className="space-y-4">
                <h1 className="font-serif text-2xl sm:text-[32px] font-medium leading-snug">{property.title}</h1>
                <ListingQualityBanner
                  report={property.qualityReport}
                  visible={property.qualityVisible}
                  editHref={`/edit-property/${property.id}`}
                />
              </div>
            )}

            {!isOwnerView && matchScore !== null && (
              <div className={`rounded-xl border p-5 ${matchScoreClasses(matchScore).box}`}>
                <div className="flex items-center gap-3">
                  <div className={`text-2xl font-bold ${matchScoreClasses(matchScore).number}`}>{matchScore}%</div>
                  <div>
                    <p className="text-[14px] font-medium">Match Score</p>
                    <p className="text-[13px] text-muted-foreground">Based on your profile preferences</p>
                  </div>
                </div>
                {matchBreakdown.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/60">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        Match Breakdown
                      </p>
                      <Badge variant="secondary" className="rounded-full text-[10px]">
                        {matchBreakdown.filter((b) => b.matched).length} / {matchBreakdown.length} matched
                      </Badge>
                    </div>
                    <ul className="space-y-2">
                      {matchBreakdown.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[14px]">
                          <span
                            className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                              item.matched
                                ? "bg-success/15 text-success"
                                : "bg-destructive/10 text-destructive"
                            }`}
                          >
                            {item.matched ? (
                              <Check className="h-3 w-3" strokeWidth={3} />
                            ) : (
                              <XIcon className="h-3 w-3" strokeWidth={3} />
                            )}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium ${item.matched ? "text-foreground" : "text-muted-foreground"}`}>
                              {item.label}
                            </p>
                            {item.detail && (
                              <p className="text-[12px] text-muted-foreground leading-snug">{item.detail}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-x-8 gap-y-4 py-6 border-y">
              <Stat icon={<Bed className="h-5 w-5" />} value={property.bedrooms} label="Bedrooms" />
              <Stat icon={<Bath className="h-5 w-5" />} value={property.bathrooms} label="Bathrooms" />
              {property.maxGuests && <Stat icon={<Users className="h-5 w-5" />} value={property.maxGuests} label="Max Guests" />}
              {property.size && <Stat icon={<Ruler className="h-5 w-5" />} value={`${property.size}m²`} label="Size" />}
            </div>

            <div>
              <h2 className="font-serif text-xl mb-4">About This Property</h2>
              <p className="text-muted-foreground leading-[1.7] text-[15px]">{property.description}</p>
            </div>

            {/* Bedroom Details */}
            {property.bedroomDetails.length > 0 && (
              <div>
                <h2 className="font-serif text-xl mb-4">Bedrooms</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {property.bedroomDetails.map((bd, i) => (
                    <div key={i} className="bg-card rounded-xl border p-5 shadow-soft">
                      <h3 className="text-[14px] font-semibold mb-2">Bedroom #{i + 1}</h3>
                      <p className="text-[14px] text-muted-foreground flex items-center gap-2">
                        <Bed className="h-4 w-4 text-primary" /> {bedsLabel(bedsIn(bd))}
                      </p>
                      {bd.ensuite && (
                        <p className="text-[13px] text-muted-foreground flex items-center gap-2 mt-1">
                          <Bath className="h-3.5 w-3.5 text-primary" /> Ensuite bathroom
                        </p>
                      )}
                      {bd.description && (
                        <p className="text-[13px] text-muted-foreground mt-2">{bd.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="font-serif text-xl mb-4">Amenities</h2>
              {amenityGroups.length === 0 ? (
                <p className="text-[14px] text-muted-foreground">No amenities listed yet</p>
              ) : (
                <div className="space-y-5">
                  {amenityGroups.map((g) => (
                    <div key={g.id}>
                      <h3 className="mb-1 text-[12px] uppercase tracking-wide text-muted-foreground">{g.label}</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
                        {g.items.map((a) => {
                          // "More homes with a dishwasher in Santa Teresa": the chip
                          // links to its collection when one is published.
                          const target = collectionForAmenity(collections, a.label, browseCtx);
                          const inner = (
                            <>
                              <AmenityIcon name={a.icon} className="h-4 w-4 shrink-0 text-primary" />
                              <span className="min-w-0 break-words">{a.label}</span>
                            </>
                          );
                          return target ? (
                            <Link
                              key={a.key}
                              to={collectionPath(target.slug)}
                              title={`More homes with ${a.label.toLowerCase()} in ${tenant.cityLabel}`}
                              className="flex items-center gap-2 text-[14px] text-muted-foreground py-1.5 min-w-0 hover:text-primary underline-offset-4 hover:underline"
                            >
                              {inner}
                            </Link>
                          ) : (
                            <div key={a.key} className="flex items-center gap-2 text-[14px] text-muted-foreground py-1.5 min-w-0">
                              {inner}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {notIncluded.length > 0 && (
                <p className="mt-4 text-[13px] text-muted-foreground">
                  <span className="font-medium text-foreground">Not included:</span> {notIncluded.join(" · ")}
                </p>
              )}
            </div>

            {/* House rules: pets, and smoking once the owner has answered it
                (null = never asked, so nothing is claimed for them). */}
            {(property.petFriendly || property.amenities.some(isPetOption) || property.smokingAllowed != null) && (
              <div>
                <h2 className="font-serif text-xl mb-4">House rules</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {property.petFriendly && (
                    <div className="flex items-center gap-2 text-[14px] text-muted-foreground py-1.5">
                      <Check className="h-4 w-4 text-primary" />Pets allowed
                    </div>
                  )}
                  {property.amenities.filter(isPetOption).map((p) => (
                    <div key={p} className="flex items-center gap-2 text-[14px] text-muted-foreground py-1.5">
                      <Check className="h-4 w-4 text-primary" />{p}
                    </div>
                  ))}
                  {property.smokingAllowed === true && (
                    <div className="flex items-center gap-2 text-[14px] text-muted-foreground py-1.5">
                      <Check className="h-4 w-4 text-primary" />Smoking allowed
                    </div>
                  )}
                  {property.smokingAllowed === false && (
                    <div className="flex items-center gap-2 text-[14px] text-muted-foreground py-1.5">
                      <CigaretteOff className="h-4 w-4 text-muted-foreground" />No smoking
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <h2 className="font-serif text-xl mb-4">Services Included in Rent</h2>
              {activeServices.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {activeServices.map((s) => (
                    <div key={s} className="flex items-center gap-2 text-[14px] text-muted-foreground py-1.5">
                      <Check className="h-4 w-4 text-primary" />
                      <span>
                        {s}
                        {s === "Cleaning" && property.cleaningDaysPerWeek != null && (
                          <span className="text-[12px] text-muted-foreground ml-1">
                            ({property.cleaningDaysPerWeek} day{property.cleaningDaysPerWeek > 1 ? "s" : ""}/week)
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[14px] text-muted-foreground">No services included</p>
              )}
            </div>

            <div>
              <h2 className="font-serif text-xl mb-4">Rental Terms</h2>
              <div className="space-y-3">
                <IncludedRow label="Minimum Rental" text={minRentalLabel} />
                <IncludedRow label="Deposit" text={property.included.deposit} />
                {property.parkingSpots > 0 && (
                  <IncludedRow label="Parking" text={`${property.parkingSpots} spot${property.parkingSpots > 1 ? "s" : ""}`} />
                )}
                {property.walkingDistanceToBeachMin != null && (
                  <IncludedRow label="Walk to beach" text={`${property.walkingDistanceToBeachMin} min`} />
                )}
              </div>
            </div>

            <AvailabilitySection
              user={user}
              property={property}
              initialMonth={stay.moveIn ?? null}
            />

            {property.coordinates && (
              <div>
                <h2 className="font-serif text-xl mb-4">Location</h2>
                {/* Defer the Google Maps script + map render until the
                    user scrolls near this section, Maps adds ~270KB
                    of JS and ~2s of CPU work that's wasted if the user
                    never reaches the Location block. */}
                <LazyOnView
                  placeholder={
                    <div className="rounded-card overflow-hidden border h-[304px] w-full bg-muted/30" />
                  }
                >
                  <PropertyMap coordinates={property.coordinates} title={property.title} />
                </LazyOnView>
              </div>
            )}
          </div>

          {!isOwnerView && (
            <div className="lg:col-span-1">
              <div id="contact-owner-card" className="sticky top-nav pt-6 space-y-5 scroll-mt-20">
                <div className="bg-card rounded-card border p-5 sm:p-6 space-y-4 shadow-elevated">
                  <p className="text-2xl font-semibold text-primary">{priceLabel}{priced && <span className="text-[14px] font-normal text-muted-foreground">/month</span>}</p>
                  <p className="text-[14px] text-muted-foreground">Minimum stay: {minRentalLabel}</p>

                  {!user ? (
                    <div>
                      <Link to={`/login?mode=signin&redirect=${encodeURIComponent(`/properties/${property.slug}`)}`}><Button className="w-full gap-2" size="lg"><Lock className="h-4 w-4" /> Sign in to contact owner</Button></Link>
                      <p className="text-[12px] text-muted-foreground text-center mt-2">Create a profile to see contact details</p>
                    </div>
                  ) : requestStatus === "pending" ? (
                    <div className="bg-ocean-light rounded-xl border border-primary/20 p-4 text-center">
                      <Clock className="h-5 w-5 text-primary mx-auto mb-2" />
                      <p className="text-[14px] font-medium text-primary">Request Sent</p>
                      <p className="text-[12px] text-muted-foreground mt-1">Waiting for owner response.</p>
                      <IdentityOffer className="mt-3 text-left" who="renter" surface="request_sent" context="Owners see requests from verified renters first." />
                    </div>
                  ) : requestStatus === "accepted" ? (
                    <div className="bg-jungle-light rounded-xl border border-primary/20 p-4 text-center">
                      <Check className="h-5 w-5 text-primary mx-auto mb-2" />
                      <p className="text-[14px] font-medium text-primary">Request Accepted!</p>
                      <p className="text-[12px] text-muted-foreground mt-1">If accepted, you will be notified and the owner/agent will soon contact you over email or Whatsapp</p>
                    </div>
                  ) : previewMode ? (
                    <div className="bg-muted rounded-xl p-4 text-center">
                      <p className="text-[13px] text-muted-foreground">Renters see the request form here.</p>
                    </div>
                  ) : requestStatus === "declined" ? (
                    <div className="bg-muted rounded-xl p-4 text-center">
                      {declinedForRules ? (
                        <p className="text-sm font-medium text-muted-foreground">
                          Not sent: requests are for renting a home only.{" "}
                          <Link to="/terms" className="text-primary-ink underline underline-offset-4">Rules</Link>
                        </p>
                      ) : (
                        <p className="text-[14px] font-medium text-muted-foreground">Request Declined</p>
                      )}
                    </div>
                  ) : requestStatus === "closed" && !showContactRequest ? (
                    <div className="bg-muted rounded-xl p-4 text-center space-y-2">
                      <p className="text-[14px] font-medium text-foreground">Your request was closed</p>
                      <p className="text-[12px] text-muted-foreground">
                        {closedRequest?.reason === "availability_changed"
                          ? "The owner changed the availability and your dates no longer fit. Pick new dates and send it again."
                          : "The home was off the market for a while. It’s back, so you can send a new request."}
                      </p>
                      <Button className="w-full" size="lg" onClick={() => setShowContactRequest(true)}>Request again</Button>
                    </div>
                  ) : (
                    <>
                      {!hasFreeDates ? (
                        <div className="bg-muted rounded-xl p-4 text-center" data-testid="no-free-dates">
                          <p className="text-[14px] font-medium text-foreground">No free dates right now</p>
                          <p className="text-[12px] text-muted-foreground mt-1">The owner has blocked every date in the next 18 months.</p>
                        </div>
                      ) : !showContactRequest ? (
                        <>
                          <Button className="w-full" size="lg" onClick={() => setShowContactRequest(true)}>
                            Send a message to the owner
                          </Button>
                          <p className="text-[12px] text-muted-foreground text-center">The owner will review your profile and respond</p>
                        </>
                      ) : (
                        <div className="space-y-3">
                          <StayPicker source={property} value={stay} onChange={setStay} />
                          {/* The profile already holds the intro, people and pets: the renter
                              only confirms (Oct 12 check-in). A note is optional. Oct 28: shown
                              as what the owner already gets, apart from the note, so nobody
                              retypes their intro into the note. */}
                          {renterProfile?.introduction ? (
                            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] dark:border-amber-700 dark:bg-amber-950/30" data-testid="request-shared-info">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300 mb-1">Information already shared with the owner</p>
                              <p className="line-clamp-3">{renterProfile.introduction}</p>
                              {(renterProfile.num_people != null || renterProfile.has_pets != null) && (
                                <p className="mt-1 text-[12px] text-amber-900/80 dark:text-amber-200/80">
                                  {[
                                    renterProfile.num_people != null ? `${renterProfile.num_people} ${renterProfile.num_people === 1 ? "person" : "people"}` : null,
                                    renterProfile.has_pets != null ? (renterProfile.has_pets ? "with pets" : "no pets") : null,
                                  ].filter(Boolean).join(", ")}, and your dates above.
                                </p>
                              )}
                            </div>
                          ) : null}
                          <Textarea
                            value={requestMessage}
                            onChange={(e) => { setRequestMessage(e.target.value); setRequestError(null); }}
                            aria-invalid={!!requestError}
                            aria-describedby={requestError ? "request-message-error" : undefined}
                            placeholder={renterProfile?.introduction
                              ? "Add a note for this home (optional)"
                              : "Introduce yourself: who you are and why this place feels right for you..."}
                            rows={renterProfile?.introduction ? 2 : 4}
                            className="rounded-lg resize-none text-[14px]"
                          />
                          {requestError && (
                            <p id="request-message-error" role="alert" className="text-sm font-medium text-destructive">{requestError}</p>
                          )}
                          {!renterProfile?.introduction && (
                            <p className="text-[12px] text-muted-foreground">
                              Your dates above, and your people and pets from your profile, go to the owner.
                            </p>
                          )}
                          {/* Oct 19: what requests are for, before Send (Site copy request.rules-note). */}
                          <p className="text-sm text-muted-foreground" data-testid="request-rules-note">
                            {rulesNote}{" "}
                            <Link to="/terms" className="text-primary-ink underline underline-offset-4">Rules</Link>
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => { setShowContactRequest(false); setRequestMessage(""); }}
                              disabled={sendingRequest}
                            >
                              Cancel
                            </Button>
                            <Button className="flex-1" onClick={handleContactRequest} disabled={sendingRequest || !stay.moveIn || !stayFits(property, stay)}>
                              {sendingRequest ? "Sending..." : "Send message"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {requestStatus === "none" && user && (
                    <div className="border-t pt-4 mt-2">
                      <p className="text-[13px] font-medium text-muted-foreground mb-2">What happens next?</p>
                      <ol className="text-[13px] text-muted-foreground space-y-1.5 list-decimal list-inside">
                        <li>You send a request with a short intro</li>
                        <li>The owner reviews your profile</li>
                        <li>If accepted, you will be notified and the owner/agent will soon contact you over email or Whatsapp</li>
                      </ol>
                    </div>
                  )}
                </div>

                {ownerProfile && (
                  <section className="border-t border-border/60 pt-6 mt-2">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-3">Managed by</p>
                    <div className="flex items-center gap-3 mb-3">
                      {ownerProfile.avatar_url ? (
                        <img src={resolveStorageUrl(ownerProfile.avatar_url)} alt="" className="h-10 w-10 rounded-full object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                          {(ownerProfile.display_name ?? "O")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-[15px] truncate">{ownerProfile.display_name ?? "Property Owner"}</p>
                      </div>
                    </div>
                    {ownerProfile.bio && (
                      <p className="text-[14px] text-muted-foreground leading-relaxed">{ownerProfile.bio}</p>
                    )}
                  </section>
                )}
              </div>
            </div>
          )}
        </div>

        {validDraft && (
          <aside className="mt-10 rounded-card border bg-sand2-soft p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-sand2 p-2.5 flex-shrink-0">
                <Sparkles className="h-4 w-4 text-foreground/70" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Draft preview</p>
                <h3 className="font-serif text-lg sm:text-xl mt-1">This is your listing</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Claim it to take ownership and start receiving requests.
                </p>
                <Link to={`/claim-listing?token=${draftToken}`}>
                  <Button className="rounded-full">Claim this listing</Button>
                </Link>
              </div>
            </div>
          </aside>
        )}

        {isOwnerView && (
          <DelistDialog
            open={delistOpen}
            onOpenChange={setDelistOpen}
            listing={{
              id: property.id,
              title: property.title,
              status: currentStatus,
              owner_id: property.owner_id ?? null,
              tenant_id: (property as { tenant_id?: string | null }).tenant_id ?? null,
              photos: (property as { photos?: unknown }).photos,
              image_urls: property.images,
            }}
            intent="pause"
            onDone={(o) => {
              if (o.action === "deleted") { navigate("/dashboard"); return; }
              if (o.action === "paused") setLocalStatus("unlisted");
              if (o.action === "rented") setLocalStatus("rented");
              if (o.action === "kept_published") setLocalStatus("available");
            }}
          />
        )}

        {/* Quick-complete gate: collects only the tenant-required fields the
            renter is missing, saves them, then sends the message they already
            typed. */}
        <CompleteProfileDialog
          open={completeProfileOpen}
          onOpenChange={setCompleteProfileOpen}
          missing={missingFields}
          initialValues={{
            displayName: renterProfile?.display_name ?? "",
            phone: renterPhone ?? "",
            introduction: renterProfile?.introduction ?? "",
          }}
          onCompleted={(saved) => {
            setRenterProfile((p) => ({
              ...(p ?? {}),
              display_name: saved.displayName,
              introduction: saved.introduction,
              profile_completed: true,
            }));
            setRenterPhone(saved.phone);
            submitContactRequest();
          }}
        />
      </div>
    </div>
  );
};

const Stat = ({ icon, value, label }: { icon: React.ReactNode; value: string | number; label: string }) => (
  <div className="flex items-center gap-2">
    <div className="text-primary">{icon}</div>
    <div>
      <p className="font-semibold text-[15px]">{value}</p>
      <p className="text-[12px] text-muted-foreground">{label}</p>
    </div>
  </div>
);

const IncludedRow = ({ label, value, text }: { label: string; value?: boolean; text?: string }) => (
  <div className="flex justify-between items-center text-[14px]">
    <span className="text-muted-foreground">{label}</span>
    {text ? <span className="font-medium">{text}</span> : value ? <span className="text-primary flex items-center gap-1"><Check className="h-4 w-4" /> Included</span> : <span className="text-muted-foreground flex items-center gap-1"><XIcon className="h-4 w-4" /> Not included</span>}
  </div>
);

/**
 * Renter-facing availability calendar. Shows two months at a time with
 * the owner's blocked dates struck through and disabled. Past dates are
 * also disabled. The view starts on the first month that contains a
 * future blocked date so the most relevant info is on screen first.
 */
const AvailabilitySection = ({
  user,
  property,
  initialMonth,
}: {
  user: { id: string } | null;
  property: Property;
  initialMonth: Date | null;
}) => (
  <div>
    <h2 className="font-serif text-xl mb-4">Availability</h2>
    {user ? (
      <AvailabilityCalendar source={property} initialMonth={initialMonth} />
    ) : (
      <div className="relative min-h-[22rem] rounded-xl border bg-card overflow-hidden">
        <div className="absolute inset-0 bg-muted/60 blur-sm pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/60 px-6 text-center">
          <Lock className="h-5 w-5 text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground">Sign up to see available dates</p>
          <Link to={`/login?mode=signup&redirect=${encodeURIComponent(`/properties/${property.slug}`)}`}>
            <Button size="sm" className="rounded-full">Sign up free</Button>
          </Link>
        </div>
      </div>
    )}
  </div>
);

export default PropertyDetail;
